import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from './AnimatedSection';
const primaryOrgs = [{
  name: 'Pet Tech',
  caption: 'Certified Training Framework',
  logo: 'https://cdn.magicpatterns.com/uploads/bWubciDcZanU62kUHAhRjR/Pet-Tech-Logo-Horizontal.png'
}, {
  name: 'PACCC',
  caption: '7 CEUs / Continuing Education Value',
  logo: 'https://cdn.magicpatterns.com/uploads/aE1ZRZtHjqyz4jmjGiPa66/PACCC_logo.png'
}];
const ecosystemOrgs = [{
  name: 'IBPSA',
  caption: 'Boarding & Pet Services',
  logo: 'https://cdn.magicpatterns.com/uploads/8tsCbXRpaFmWUpwW2sjzTH/IBPSA_logo.png'
}, {
  name: 'PSI',
  caption: 'Pet Sitters International',
  logo: 'https://cdn.magicpatterns.com/uploads/a367i1iDWYLi6fxg3RDhYH/psi_logo.png'
}, {
  name: 'NAPPS',
  caption: 'Professional Pet Sitters',
  logo: 'https://cdn.magicpatterns.com/uploads/bYWEUnd9mjF84Cq2D4nhZX/NAPPS_logo.png'
}, {
  name: 'APDT',
  caption: 'Dog Training Professionals',
  logo: 'https://cdn.magicpatterns.com/uploads/d1RKuUa6jDMPPiwdbCQYJg/APDT_logo.png'
}, {
  name: 'IACP',
  caption: 'Canine Professionals',
  logo: 'https://cdn.magicpatterns.com/uploads/gKzeuNWeJwdL9h7braQXm6/IACP_logo.png'
}];
const industryCategories = ['Groomer Associations', 'Pet Franchises', 'Business Insurers'];
export function HeroTrustBar() {
  // Duplicate for seamless marquee loop
  const marqueeLogos = [...ecosystemOrgs, ...ecosystemOrgs];
  return <AnimatedSection className="py-14 lg:py-16 bg-warm-gray-50 border-b border-warm-gray-100" data-id="element-126">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
        {/* Title — smaller text */}
        <div className="text-center mb-10" data-id="element-128">
          <h2 className="text-lg lg:text-xl font-heading text-primary-900 mb-2" data-id="element-129">
            Built on Recognized Training Standards
          </h2>
          <p className="text-warm-gray-400 font-body text-xs lg:text-sm max-w-xl mx-auto" data-id="element-130">
            Training connected to certification standards and professional
            organizations across the pet care industry.
          </p>
        </div>

        {/* ROW 1 — Primary Authority (static, large logos, full color) */}
        <div className="flex justify-center items-center gap-14 lg:gap-24 mb-10" data-id="element-131">
          {primaryOrgs.map((org, i) => <motion.div key={org.name} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="flex flex-col items-center" data-id="element-132">
              <img src={org.logo} alt={`${org.name} logo`} className="h-20 lg:h-28 w-auto object-contain" data-id="element-133" />
              <span className="mt-2 font-body text-[10px] lg:text-xs text-warm-gray-400" data-id="element-134">
                {org.caption}
              </span>
            </motion.div>)}
        </div>

        {/* Divider */}
        <div className="w-12 h-px bg-warm-gray-200 mx-auto mb-10" data-id="element-135"></div>

        {/* ROW 2 — Scrolling Marquee (full color, large) */}
        <div className="relative overflow-hidden mb-10" data-id="element-136">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-16 lg:w-24 bg-gradient-to-r from-warm-gray-50 to-transparent z-10 pointer-events-none" data-id="element-137"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 lg:w-24 bg-gradient-to-l from-warm-gray-50 to-transparent z-10 pointer-events-none" data-id="element-138"></div>

          <div className="flex animate-marquee items-center" data-id="element-139">
            {marqueeLogos.map((org, i) => <div key={`${org.name}-${i}`} className="flex-shrink-0 flex flex-col items-center mx-8 lg:mx-12" data-id="element-140">
                <img src={org.logo} alt={`${org.name} logo`} className="h-12 lg:h-16 w-auto object-contain" data-id="element-141" />
                <span className="mt-1.5 font-body text-[9px] lg:text-[10px] text-warm-gray-400 whitespace-nowrap" data-id="element-142">
                  {org.caption}
                </span>
              </div>)}
          </div>
        </div>

        {/* ROW 3 — Industry Categories (text pills, smaller) */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-143">
          {industryCategories.map((cat, i) => <motion.span key={cat} initial={{
          opacity: 0,
          y: 8
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: 0.3 + i * 0.08
        }} className="inline-flex items-center px-3 py-1.5 rounded-full border border-warm-gray-200 bg-transparent font-body text-[10px] text-warm-gray-400" data-id="element-144">
              {cat}
            </motion.span>)}
        </div>

        {/* Disclaimer */}
        <p className="text-center font-body text-[10px] text-warm-gray-300 max-w-xl mx-auto leading-relaxed" data-id="element-145">
          Organizations shown represent relevant associations and professional
          bodies within the broader pet care ecosystem. PawVerse is not claiming
          direct endorsement by each organization shown.
        </p>
      </div>

      {/* Marquee keyframes */}
      <style data-id="element-146">{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </AnimatedSection>;
}